import { DocumentType } from '@lib/ged/document-types.type';
import {
  HabilitationDocumentCode,
  HabilitationLegalStatus,
  HabilitationParcours,
} from './habilitation-parcours.model';
import { getHabilitationDocumentPresentation } from './habilitation-parcours-presentation';

export interface HabilitationDocumentProjection {
  documentType: DocumentType;
  label: string;
}

const DOCUMENT_TYPE_BY_PARCOURS_CODE: Partial<
  Record<HabilitationDocumentCode, DocumentType>
> = {
  diplome_master_2: 'diplome',
  equivalence_pbs: 'attestation_formation',
  attestation_equivalence_formation_cif: 'attestation_formation',
  attestation_equivalence_formation_alpsi: 'attestation_formation',
  rcp: 'rcp',
  rsac: 'rsac',
  kbis_pl: 'kbis_pl',
  kbis_soc: 'kbis_soc',
  carte_t: 'carte_transaction',
  cni: 'cni',
  photo_identite: 'photo_identite',
  passeport: 'passeport',
  casier_judiciaire: 'casier_judiciaire_b3',
};

const NON_PRELOADABLE_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set([
  'diplome',
  'attestation_formation',
]);
export function getHabilitationDocumentType(
  code: HabilitationDocumentCode,
  legalStatus: HabilitationLegalStatus
): DocumentType | undefined {
  if (code !== 'rib') return DOCUMENT_TYPE_BY_PARCOURS_CODE[code];

  switch (legalStatus) {
    case 'agent_commercial':
      return 'rib_agc';
    case 'profession_liberale':
      return 'rib_pl';
    case 'societe':
      return 'rib_soc';
  }
}

export function getHabilitationDocumentCandidateTypes(
  code: HabilitationDocumentCode
): DocumentType[] {
  if (code !== 'rib') {
    const documentType = DOCUMENT_TYPE_BY_PARCOURS_CODE[code];
    return documentType ? [documentType] : [];
  }

  return ['rib_agc', 'rib_pl', 'rib_soc'];
}

export function getHabilitationDocumentProjectionsFromParcours(
  parcours: HabilitationParcours | undefined,
  legalStatus: HabilitationLegalStatus
): HabilitationDocumentProjection[] {
  if (!parcours) return [];

  return (parcours.documents ?? []).flatMap(requirement => {
    const documentType = getHabilitationDocumentType(requirement.code, legalStatus);
    const presentation = getHabilitationDocumentPresentation(
      parcours.type,
      requirement.code
    );

    return documentType && presentation
      ? [{ documentType, label: presentation.label }]
      : [];
  });
}

export function getHabilitationDocumentPreloadProjectionsFromParcours(
  parcours: HabilitationParcours | undefined,
  legalStatus: HabilitationLegalStatus
): HabilitationDocumentProjection[] {
  return getHabilitationDocumentProjectionsFromParcours(parcours, legalStatus).filter(
    projection => !NON_PRELOADABLE_DOCUMENT_TYPES.has(projection.documentType)
  );
}
